import { setAccessToken } from './tokenStore';

/**
 * Accepting a read-only impersonation session from the admin portal (slice 17c).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS IS THE SEAM BETWEEN TWO TREES, WHICH IS WHY IT IS ITS OWN FILE.
 * ─────────────────────────────────────────────────────────────────────────────
 * `admin/` opens `https://app.raadtelematics.com/#impersonate=<token>`. Nothing
 * in `web/` knew what that meant until this module existed, and a "Open portal"
 * button that opened a dashboard which ignored the token would have been a
 * control that does nothing — worse than no control at all.
 *
 * CLAUDE.md's rule about seams is the reason this is written down rather than
 * inlined: a defect here survives every per-tree check, because each tree is
 * internally consistent and the work is simply not there.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔴 THE FRAGMENT IS CONSUMED AND ERASED IN THE SAME BREATH.
 * ─────────────────────────────────────────────────────────────────────────────
 * A fragment is never sent to the server, which is why the token travels there
 * rather than in a query string — it stays out of access logs, Referer headers
 * and every proxy in between. But it IS visible in the address bar and it WOULD
 * be captured by a bookmark or a copied URL, so it is removed from history the
 * moment it is read, before anything else can happen.
 *
 * `replaceState`, not a navigation: the entry is REWRITTEN rather than a new one
 * pushed, so the token cannot be reached with the back button either.
 */

const FRAGMENT_KEY = 'impersonate';

/**
 * Read an impersonation token out of the URL fragment, install it, and scrub the
 * fragment. Returns true when a session was adopted.
 *
 * Called BEFORE the ordinary refresh-cookie bootstrap, because an impersonation
 * token must win: a staff member opening a customer's portal in a browser where
 * they happen to have their own session would otherwise see their own fleet and
 * believe they were looking at the customer's.
 */
export function adoptImpersonationFromFragment(): boolean {
  const hash = window.location.hash;
  if (hash.length < 2) return false;

  const params = new URLSearchParams(hash.slice(1));
  const token = params.get(FRAGMENT_KEY);
  if (token === null || token === '') return false;

  setAccessToken(token);

  // Scrub immediately. Everything after this point runs with the token in
  // memory and nothing in the URL.
  params.delete(FRAGMENT_KEY);
  const rest = params.toString();
  window.history.replaceState(
    null,
    '',
    `${window.location.pathname}${window.location.search}${rest === '' ? '' : `#${rest}`}`,
  );

  return true;
}

/**
 * Whether the current session is a read-only impersonation.
 *
 * Read from the token's own `ro` claim rather than from a flag set alongside it,
 * so the banner cannot disagree with what the API will actually permit. The
 * decode is unverified — acceptable because this decides only what to SHOW. The
 * enforcement is server-side in `authenticate`, and this never grants anything.
 *
 * 🔴 Never make an ALLOW decision from this. It is display state.
 */
export function readImpersonationClaims(
  token: string | null,
): { readOnly: boolean; tenantId: string | null } {
  if (token === null) return { readOnly: false, tenantId: null };
  try {
    const [, body] = token.split('.');
    if (body === undefined) return { readOnly: false, tenantId: null };
    const json = JSON.parse(atob(body.replace(/-/g, '+').replace(/_/g, '/'))) as {
      ro?: unknown;
      tid?: unknown;
    };
    return {
      readOnly: json.ro === true,
      tenantId: typeof json.tid === 'string' ? json.tid : null,
    };
  } catch {
    return { readOnly: false, tenantId: null };
  }
}
